/**
 * Recuperação de empresa ativa OBSOLETA (shared-account-v2 F1).
 *
 * Desde a F1 o backend valida o `X-Company-Id`: header de uma empresa em que o
 * usuário não é mais membro (removido, empresa suspensa) responde 403
 * `COMPANY_NOT_ALLOWED` — antes o CRM caía em 401/"selecione uma empresa". A
 * empresa ativa vive persistida, então o 403 aparece quando o store ficou para
 * trás. Reação única: esquece a empresa ativa, invalida o cache e recarrega a
 * lista de empresas (o store escolhe uma permitida).
 */
import { isAxiosError, type AxiosInstance } from 'axios';
import { useCompanyStore } from '@/stores/companyStore';
import { queryClient } from '@/lib/query-client';

export const COMPANY_NOT_ALLOWED = 'COMPANY_NOT_ALLOWED';

export function isCompanyNotAllowedError(error: unknown): boolean {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return error.response?.status === 403 && data?.code === COMPANY_NOT_ALLOWED;
}

async function refetchCompanies(): Promise<void> {
  // Import dinâmico: `companies` importa o cliente HTTP, que instala esta
  // recuperação — um import estático fecharia o ciclo de módulos.
  const { companiesService } = await import('@/services/companies');
  useCompanyStore.getState().setCompanies(await companiesService.listMine());
}

/** Devolve `false` quando não havia empresa ativa (evita laço se o 403 se repetir). */
export function resetStaleCompanyContext(): boolean {
  const store = useCompanyStore.getState();
  if (!store.activeCompany) return false;
  store.setActiveCompany(null);
  void queryClient.invalidateQueries();
  refetchCompanies().catch((error: unknown) => {
    // oprus-allow:swallow — a empresa obsoleta já foi esquecida (o que destrava
    // o usuário); a lista é recarregada de novo no próximo carregamento do app.
    console.warn('[company-context] falha ao recarregar empresas após COMPANY_NOT_ALLOWED', error);
  });
  return true;
}

/** Instala a recuperação num cliente axios. O erro segue para quem chamou. */
export function installCompanyContextRecovery(instance: AxiosInstance): void {
  instance.interceptors.response.use(undefined, (error: unknown) => {
    if (isCompanyNotAllowedError(error)) resetStaleCompanyContext();
    return Promise.reject(error);
  });
}
